import React, { useContext, useEffect, useMemo, useState } from 'react';
import { min, max } from 'lodash';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import axiosInstance from '../utils/axiosInstance';
import { API_URL } from '../utils/runtimeConfig';
import { AuthContext } from '../context/AuthContext';
import MultiSelectFilter from './MultiSelectFilter';
import './css/CommanderHistoryView.css';

const apiUrl = API_URL;

const getStatisticValue = (entry, selectedStatistic) => {
  if (selectedStatistic.startsWith('rankingData.')) {
    const pathParts = selectedStatistic.split('.');
    if (pathParts.length === 3) {
      const [, category, field] = pathParts;
      return entry.rankingData?.[category]?.[field] ?? null;
    }
    if (pathParts.length === 2) {
      const [, field] = pathParts;
      return entry.rankingData?.[field] ?? null;
    }
  }

  return entry?.[selectedStatistic] ?? null;
};

const CommanderHistoryView = ({ initialCommanderIds = [], initialNationId = '' }) => {
  const { user } = useContext(AuthContext);
  const isGlobalHistoryRole = user?.role === 'superUser' || user?.role === 'supporter';
  const userNationId = typeof user?.nation === 'string' ? user.nation : user?.nation?._id || '';

  const [historyRecords, setHistoryRecords] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [selectedCommanders, setSelectedCommanders] = useState(initialCommanderIds);
  const [selectedAlliances, setSelectedAlliances] = useState([]);
  const [selectedStatistic, setSelectedStatistic] = useState('battlePower');
  const [commanderNameMap, setCommanderNameMap] = useState(new Map());
  const [commanderAllianceMap, setCommanderAllianceMap] = useState(new Map());
  const [commanderOptions, setCommanderOptions] = useState([]);
  const [allianceOptions, setAllianceOptions] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [nations, setNations] = useState([]);
  const [selectedNationId, setSelectedNationId] = useState(
    isGlobalHistoryRole ? initialNationId || String(userNationId || '') : ''
  );
  const [includeOrphans, setIncludeOrphans] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSelectedCommanders(initialCommanderIds);
  }, [initialCommanderIds]);

  useEffect(() => {
    if (!isGlobalHistoryRole) return;

    const fetchNations = async () => {
      try {
        const response = await axiosInstance.get(`${apiUrl}/nations`);
        setNations(response.data || []);
      } catch (error) {
        console.error('Error fetching nations for history filters:', error);
      }
    };

    fetchNations();
  }, [isGlobalHistoryRole]);

  useEffect(() => {
    const fetchCommanderHistory = async () => {
      try {
        setIsLoading(true);
        const queryParams = new URLSearchParams();
        if (isGlobalHistoryRole && selectedNationId) {
          queryParams.set('nationId', selectedNationId);
        }
        queryParams.set('includeCurrent', 'true');
        queryParams.set('includeOrphans', includeOrphans ? 'true' : 'false');

        const query = queryParams.toString();
        const url = query
          ? `${apiUrl}/commanders/history?${query}`
          : `${apiUrl}/commanders/history`;

        const res = await axiosInstance.get(url);
        const historyData = res.data.history || [];

        setHistoryRecords(historyData);
      } catch (error) {
        console.error('Error fetching commander history:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCommanderHistory();
  }, [isGlobalHistoryRole, selectedNationId, includeOrphans]);

  useEffect(() => {
    const latestNameByCommander = new Map();
    const allianceLookup = new Map();
    const allianceByCommander = new Map();

    historyRecords.forEach((entry) => {
      const commanderId = String(entry.commanderId);
      if (!latestNameByCommander.has(commanderId) || new Date(entry.modifiedAt) > new Date(latestNameByCommander.get(commanderId).modifiedAt)) {
        latestNameByCommander.set(commanderId, {
          name: entry.name,
          modifiedAt: entry.modifiedAt
        });
      }

      if (!allianceByCommander.has(commanderId)) {
        allianceByCommander.set(commanderId, new Set());
      }
      if (entry.alliance?._id) {
        allianceByCommander.get(commanderId).add(String(entry.alliance._id));
      }

      if (entry.alliance?._id && !allianceLookup.has(entry.alliance._id)) {
        allianceLookup.set(entry.alliance._id, {
          value: entry.alliance._id,
          label: entry.alliance.acronym || entry.alliance.name || 'Unknown Alliance'
        });
      }
    });

    setCommanderNameMap(latestNameByCommander);
    setCommanderAllianceMap(allianceByCommander);
    setCommanderOptions(
      [...latestNameByCommander.entries()].map(([value, commander]) => ({
        value,
        label: commander.name
      }))
    );
    setAllianceOptions([...allianceLookup.values()].sort((a, b) => a.label.localeCompare(b.label)));
  }, [historyRecords]);

  const filteredCommanderOptions = useMemo(() => {
    const selectedAllianceSet = new Set(selectedAlliances.map((value) => String(value)));
    const selectedCommanderSet = new Set(selectedCommanders.map((value) => String(value)));

    const scopedOptions = commanderOptions.filter((option) => {
      const commanderId = String(option.value);
      if (selectedCommanderSet.has(commanderId)) return true;
      if (selectedAllianceSet.size === 0) return true;

      const allianceIds = commanderAllianceMap.get(commanderId) || new Set();
      return [...allianceIds].some((allianceId) => selectedAllianceSet.has(String(allianceId)));
    });

    const selectedScoped = scopedOptions
      .filter((option) => selectedCommanderSet.has(String(option.value)))
      .sort((a, b) => a.label.localeCompare(b.label));
    const unselectedScoped = scopedOptions
      .filter((option) => !selectedCommanderSet.has(String(option.value)))
      .sort((a, b) => a.label.localeCompare(b.label));

    return [...selectedScoped, ...unselectedScoped];
  }, [commanderOptions, commanderAllianceMap, selectedAlliances, selectedCommanders]);

  useEffect(() => {
    let nextFiltered = historyRecords;

    if (selectedCommanders.length > 0) {
      const selectedCommanderSet = new Set(selectedCommanders.map(String));
      nextFiltered = nextFiltered.filter((entry) => selectedCommanderSet.has(String(entry.commanderId)));
    }

    if (selectedAlliances.length > 0) {
      const selectedAllianceSet = new Set(selectedAlliances.map(String));
      nextFiltered = nextFiltered.filter((entry) => selectedAllianceSet.has(String(entry.alliance?._id || '')));
    }

    if (startDate) {
      const minDate = new Date(`${startDate}T00:00:00.000Z`);
      nextFiltered = nextFiltered.filter((entry) => new Date(entry.modifiedAt) >= minDate);
    }

    if (endDate) {
      const maxDate = new Date(`${endDate}T23:59:59.999Z`);
      nextFiltered = nextFiltered.filter((entry) => new Date(entry.modifiedAt) <= maxDate);
    }

    setFilteredHistory(nextFiltered);
  }, [historyRecords, selectedCommanders, selectedAlliances, startDate, endDate]);

  const selectedCommanderLineKeys = useMemo(
    () => selectedCommanders.map((commanderId) => {
      const normalizedId = String(commanderId);
      const commanderName = commanderNameMap.get(normalizedId)?.name || normalizedId;
      return `${commanderName} (${normalizedId.slice(-4)})`;
    }),
    [selectedCommanders, commanderNameMap]
  );

  const chartArray = useMemo(() => {
    if (selectedCommanders.length === 0) return [];

    const selectedCommanderSet = new Set(selectedCommanders.map((id) => String(id)));
    const lineKeyByCommanderId = new Map(
      selectedCommanders.map((commanderId) => {
        const normalizedId = String(commanderId);
        const commanderName = commanderNameMap.get(normalizedId)?.name || normalizedId;
        return [normalizedId, `${commanderName} (${normalizedId.slice(-4)})`];
      })
    );

    const entriesByCommander = new Map();
    filteredHistory.forEach((entry) => {
      const commanderId = String(entry.commanderId);
      if (!selectedCommanderSet.has(commanderId)) return;

      if (!entriesByCommander.has(commanderId)) {
        entriesByCommander.set(commanderId, []);
      }
      entriesByCommander.get(commanderId).push(entry);
    });

    const byCommanderByDate = new Map();
    const allDates = new Set();

    entriesByCommander.forEach((entries, commanderId) => {
      const currentSnapshot = entries
        .filter((entry) => entry.source === 'currentSnapshot')
        .sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt))[0];
      const currentSnapshotMs = currentSnapshot ? new Date(currentSnapshot.modifiedAt).getTime() : null;

      const perDay = new Map();

      entries.forEach((entry) => {
        const modifiedAtMs = new Date(entry.modifiedAt).getTime();
        if (Number.isNaN(modifiedAtMs)) return;

        // Avoid plotting delayed-written history as newer than actual current snapshot.
        if (
          currentSnapshotMs !== null &&
          entry.source !== 'currentSnapshot' &&
          modifiedAtMs > currentSnapshotMs
        ) {
          return;
        }

        const dateKey = new Date(
          entry.source === 'currentSnapshot' ? currentSnapshotMs : modifiedAtMs
        ).toISOString().slice(0, 10);
        const value = getStatisticValue(entry, selectedStatistic);
        if (value === null || value === undefined) return;

        const existing = perDay.get(dateKey);
        if (!existing) {
          perDay.set(dateKey, {
            value,
            modifiedAtMs,
            priority: entry.source === 'currentSnapshot' ? 2 : 1
          });
          allDates.add(dateKey);
          return;
        }

        const entryPriority = entry.source === 'currentSnapshot' ? 2 : 1;
        if (
          entryPriority > existing.priority ||
          (entryPriority === existing.priority && modifiedAtMs >= existing.modifiedAtMs)
        ) {
          perDay.set(dateKey, {
            value,
            modifiedAtMs,
            priority: entryPriority
          });
        }
      });

      byCommanderByDate.set(commanderId, perDay);
    });

    const sortedDates = [...allDates].sort((a, b) => new Date(a) - new Date(b));
    const lastValueByLine = new Map();

    return sortedDates.map((dateKey) => {
      const row = { date: dateKey };

      selectedCommanders.forEach((commanderId) => {
        const normalizedId = String(commanderId);
        const lineKey = lineKeyByCommanderId.get(normalizedId);
        if (!lineKey) return;

        const dayEntry = byCommanderByDate.get(normalizedId)?.get(dateKey);
        if (dayEntry && dayEntry.value !== null && dayEntry.value !== undefined) {
          lastValueByLine.set(lineKey, dayEntry.value);
        }

        if (lastValueByLine.has(lineKey)) {
          row[lineKey] = lastValueByLine.get(lineKey);
        }
      });

      return row;
    });
  }, [selectedCommanders, filteredHistory, selectedStatistic, commanderNameMap]);

  const allDataPoints = chartArray.flatMap((entry) =>
    selectedCommanderLineKeys
      .map((lineKey) => entry[lineKey])
      .filter((value) => Number.isFinite(value))
  );
  const yAxisMin = Math.max(0, Math.floor(min(allDataPoints) || 0));
  const yAxisMax = Math.ceil(max(allDataPoints) || 0);

  const clearFilters = () => {
    setSelectedCommanders([]);
    setSelectedAlliances([]);
    setStartDate('');
    setEndDate('');
  };

  return (
    <div className="commander-history-view">
      <div className="history-header-row">
        <h2>Commander History</h2>
        <div className="history-meta">
          <span>Records: {filteredHistory.length.toLocaleString()}</span>
        </div>
      </div>

      <div className="history-filters">
        {isGlobalHistoryRole && (
          <div className="history-filter-item">
            <label>Nation</label>
            <select value={selectedNationId} onChange={(e) => setSelectedNationId(e.target.value)}>
              <option value="">All Nations</option>
              {nations.map((nation) => (
                <option key={nation._id} value={nation._id}>
                  {nation.number}
                </option>
              ))}
            </select>
          </div>
        )}

        <MultiSelectFilter
          label="Alliances"
          options={allianceOptions}
          selected={selectedAlliances}
          setSelected={setSelectedAlliances}
          placeholder="Select alliances..."
        />

        <MultiSelectFilter
          label="Commanders"
          options={filteredCommanderOptions}
          selected={selectedCommanders}
          setSelected={setSelectedCommanders}
          placeholder="Select commanders..."
          pinSelectedToTop
        />

        <div className="history-filter-item">
          <label>Statistic</label>
          <select value={selectedStatistic} onChange={(e) => setSelectedStatistic(e.target.value)}>
            <option value="battlePower">Battle Power</option>
            <option value="cityLevel">City Level</option>
            <option value="rankingData.officer.power">Officer Power</option>
            <option value="rankingData.titan.power">Titan Power</option>
            <option value="rankingData.warplane.power">Warplane Power</option>
            <option value="rankingData.kills">Kills</option>
            <option value="rankingData.losses">Losses</option>
            <option value="rankingData.individualReputation">Reputation</option>
          </select>
        </div>

        <div className="history-filter-item">
          <label>From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>

        <div className="history-filter-item">
          <label>To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>

        <label className="history-toggle">
          <input
            type="checkbox"
            checked={includeOrphans}
            onChange={(e) => setIncludeOrphans(e.target.checked)}
          />
          Show Orphan History
        </label>

        <button className="history-clear-button" onClick={clearFilters}>Clear Filters</button>
      </div>

      <div className="history-chart-container">
        {isLoading ? (
          <div className="history-empty-state">Loading history...</div>
        ) : chartArray.length === 0 || selectedCommanderLineKeys.length === 0 ? (
          <div className="history-empty-state">Select at least one commander to plot history.</div>
        ) : (
          <ResponsiveContainer width="100%" height={430}>
            <LineChart data={chartArray} margin={{ top: 20, right: 30, left: 50, bottom: 10 }}>
              <XAxis dataKey="date" />
              <YAxis
                allowDecimals={false}
                domain={[yAxisMin, yAxisMax]}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                formatter={(value) => Number(value).toLocaleString()}
                labelFormatter={(label) => `Date: ${label}`}
              />
              <Legend />
              {selectedCommanderLineKeys.map((lineKey, index) => (
                <Line
                  key={lineKey}
                  type="monotone"
                  dataKey={lineKey}
                  stroke={`hsl(${index * 47}, 70%, 45%)`}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="history-table-container">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Nation</th>
              <th>Alliance</th>
              <th>Commander</th>
              <th>Battle Power</th>
              <th>Officer</th>
              <th>Titan</th>
              <th>Warplane</th>
            </tr>
          </thead>
          <tbody>
            {[...filteredHistory]
              .sort((a, b) => new Date(b.modifiedAt) - new Date(a.modifiedAt))
              .slice(0, 200)
              .map((entry) => (
                <tr key={entry._id}>
                  <td>{new Date(entry.modifiedAt).toLocaleDateString()}</td>
                  <td>{entry.nation?.number || 'N/A'}</td>
                  <td>{entry.alliance?.acronym || 'N/A'}</td>
                  <td>{entry.name}</td>
                  <td>{entry.battlePower?.toLocaleString() || '0'}</td>
                  <td>{entry.rankingData?.officer?.power?.toLocaleString() || '0'}</td>
                  <td>{entry.rankingData?.titan?.power?.toLocaleString() || '0'}</td>
                  <td>{entry.rankingData?.warplane?.power?.toLocaleString() || '0'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommanderHistoryView;
